import { calculateAge, MedplumClient } from "@medplum/core";
import { getPatientName } from "../Eligibility/EligibiltyService";
import { Badge, Box, Text } from "@mantine/core";
import AnimatedWaves from "../../components/AnimatedWaves";
import { showNotification } from "@mantine/notifications";
import { Questionnaire } from "@medplum/fhirtypes";

interface PatientStatusCounts {
  [key: string]: {
    patientId: string;
    lastUpdated: string;

    [status: string]: number | string;
  };
}

export const patientSearch = async (
  medplum: MedplumClient,

  patientName?: string
) => {
  let queryString = `?name=${patientName}&_count=10`;

  const Patients = await medplum.searchResources("Patient", queryString);
  return Patients;
};
export const patientResource = async (
  medplum: MedplumClient,
  count?: any,
  pageNumber?: any,
  search?: boolean,
  patientName?: string
) => {
  let queryString;

  queryString = `_count=${count}&_offset=${(pageNumber - 1) * count
    }&_sort=-_lastUpdated&_total=accurate`;

  const Patients = await medplum.searchResources("Patient", queryString);
  return Patients;
};

export const getQuestionnaireResource = async (
  medplum: MedplumClient,
  count: number,
  pageNumber: number,
  source: any
) => {
  let queryString = `_count=${count}&_offset=${(pageNumber - 1) * count
    }&_sort=-_lastUpdated&_total=accurate&source=${encodeURIComponent(source)}`;

  const questionnaireResponse = await medplum.searchResources(
    "QuestionnaireResponse",
    queryString
  );

  const statusCount = countStatusByPatient(questionnaireResponse);

  return statusCount;
};
export const getQuestionnaireResponse = async (
  medplum: MedplumClient,
  count: number,
  pageNumber: number,
  source: any
) => {
  let queryString = `_count=${count}&_offset=${(pageNumber - 1) * count
    }&_sort=-_lastUpdated&_total=accurate&source=${encodeURIComponent(source)}`;

  const questionnaireResponse = await medplum.searchResources(
    "QuestionnaireResponse",
    queryString
  );
  return questionnaireResponse;
};
export function countStatusByPatient(responses: any) {
  const patientStatusCounts: { [key: string]: any } = {};

  responses.forEach((response: any) => {
    const patientId = response.source.reference.split("/")[1];
    const status = response.status;
    const lastUpdated = response.meta?.lastUpdated;
    const questionnaireId = response.questionnaire;

    if (!patientStatusCounts[patientId]) {
      patientStatusCounts[patientId] = {
        patientId: patientId,
        lastUpdated: lastUpdated,
        status: {},
        name: response.name,
        age: response.age,
        questionnaireIds: [], // Initialize an array to store questionnaire IDs
      };
    }

    if (
      lastUpdated &&
      (!patientStatusCounts[patientId].lastUpdated ||
        new Date(lastUpdated) >
        new Date(patientStatusCounts[patientId].lastUpdated))
    ) {
      patientStatusCounts[patientId].lastUpdated = lastUpdated;
    }

    if (!patientStatusCounts[patientId].status[status]) {
      patientStatusCounts[patientId].status[status] = 0;
    }
    patientStatusCounts[patientId].status[status] += 1;

    // Add the questionnaire ID to the array if it's not already there
    if (
      !patientStatusCounts[patientId].questionnaireIds.includes(questionnaireId)
    ) {
      patientStatusCounts[patientId].questionnaireIds.push(questionnaireId);
    }
  });

  return Object.values(patientStatusCounts);
}

export function addPatientNames(patientList: any, data: any) {
  // Create a map of patient IDs to names
  const patientMap = new Map(
    patientList?.map((patient: any) => [
      patient?.id,
      { name: patient?.name, birthDate: patient?.birthDate },
    ])
  );
  // Iterate over the data array and add names based on patient IDs
  data.forEach((item: any) => {
    if (patientMap.has(item?.patientId)) {
      const patientInfo: any = patientMap.get(item?.patientId);
      item.name = patientInfo.name;
      const name = getPatientName(item?.name);
      item.name = name;

      // Get birthDate and calculate age
      const birthDate = patientInfo.birthDate;
      const age = calculateAge(birthDate);
      item.age = age;
    }
  });

  return data;
}
export const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "completed":
      return "green";
    case "In-Progress":
      return "orange";
    case "stopped":
      return "red";
    case "in-progress":
      return "orange";
    default:
      return "gray";
  }
};
export const renderStatusBadges = (status: any) => {
  const importantStatuses = ["in-progress", "completed", "stopped"];
  const allStatuses = { ...status };

  importantStatuses.forEach((key) => {
    if (!allStatuses[key]) {
      allStatuses[key] = 0;
    }
  });

  return Object.keys(allStatuses).map((key) =>
    allStatuses[key] ? (
      <Badge
        variant="outline"
        size="lg"
        radius="sm"
        key={key}
        color={getStatusColor(key)}
        sx={{ marginRight: "10px" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <Text>{`${key == "stopped" ? "Not Answered" : key} : ${allStatuses[key]
            }`}</Text>
          {key === "in-progress" && (
            <Box ml={4}>
              {/* Assuming AnimatedWaves is a valid component */}
              <AnimatedWaves />
            </Box>
          )}
        </Box>
      </Badge>
    ) : (
      ""
    )
  );
};

export const countStatus = async (medplum: MedplumClient, status: string) => {
  let queryString = `_count=20&_offset=0&_sort=-_lastUpdated&_total=accurate&status=${status}`;

  const Status = await medplum.searchResources(
    "QuestionnaireResponse",
    queryString
  );
  return Status;
};
interface QuestionnaireResponse {
  id: string;
  questionnaire: string;
  // Add other properties as needed...
}

export function extractQuestionnaireIds(data: QuestionnaireResponse[]) {
  const result: { [key: string]: string } = {};
  data.forEach((item: QuestionnaireResponse) => {
    result[item?.id] = item?.questionnaire;
  });
  return result;
}

export const extractQuestionnareResource = async (
  medplum: MedplumClient,
  count: any,
  pageNumber: any,
  ids: { [key: string]: string }
): Promise<Array<Questionnaire> | undefined> => {
  const idValues = Object.values(ids);
  const queryStrings = idValues.map((id) => `${id}`);
  const queryString = `_count=20&_id=${queryStrings}&_offset=0&_sort=-_lastUpdated&_total=accurate`;
  const response = await medplum.searchResources("Questionnaire", queryString);
  return response;
};

export const getPatientDetails = (medplum: MedplumClient, patientId: any) => {
  const queryString = `_count=20&_id=${patientId}&_offset=0&_sort=-_lastUpdated&_total=accurate`;
  const patientDetails = medplum.searchResources("Patient", queryString);
  return patientDetails;
};
interface AddressRecord {
  line: { value: string }[];
  city: String;
  state: String;
  postalCode: String;
  country: String;
}

export const getAddress = (add: AddressRecord[]) => {
  let tempArr = [];
  if (add) {
    for (let address of add) {
      tempArr = [];
      if (address.line.length > 0) {
        tempArr.push(address.line[0]);
      }
      if (address.city) {
        tempArr.push(address.city);
      }
      if (address.state) {
        tempArr.push(address.state);
      }
      if (address.postalCode) {
        tempArr.push(address.postalCode);
      }
      if (address.country) {
        tempArr.push(address.country);
      }
    }
  }

  return tempArr.join(", ");
};

export const getCardsDetails = (
  questionnaire: any[] = [],
  questionnaireResponse: any[] = []
) => {
  // Ensure both arrays are defined and are arrays
  if (!Array.isArray(questionnaire) || !Array.isArray(questionnaireResponse)) {
    throw new Error("Invalid input: both arguments must be arrays");
  }
  return questionnaireResponse.map((q) => {
    // Find the corresponding response for the current questionnaire
    const response = questionnaire.find((r) => r.id === q.questionnaire);

    // Return the combined details
    return {
      qid: response?.id,
      qrid: q.id,
      questionnaire: response?.name,
      status: response ? q.status : "No status",
      date: response ? q.meta?.lastUpdated : "No last updated info",
      Questions: "--",
      numberOfQuestions: q?.item?.length ?? response?.item?.length,
      question: response?.item,
      answer: q?.item,
    };
  });
};
export function matchQuestionsAndAnswers(data: any) {
  const matchedItems: any = [];
  const status = data?.status;
  matchedItems.push({ status: status });

  // Ensure data.question and data.answer are defined and are arrays
  if (!Array.isArray(data?.question) || !Array.isArray(data?.answer)) {
    return matchedItems; // Return an empty array if either is missing
  }

  // Iterate through each question
  data.answer.forEach((ans: any) => {
    // Find matching answer based on linkId
    const matchedQues = data.question.find(
      (ques: any) => ques?.linkId === ans?.linkId
    );
    // If a match is found, push the question and answer to the matchedItems array
    matchedItems.push({
      // status:question?.status,
      questionId: ans?.id,
      questionLinkId: ans?.linkId,
      questionText: matchedQues?.text,
      answerText: ans?.text || "--",
    });
  });

  return matchedItems;
}

export const extractQuestionnare = (
  medplum: MedplumClient,
  count: any,
  pageNumber: any,
  ids?: any
) => {
  let queryString;
  if (ids) {
    queryString = `_count=20&_id=${ids}&_offset=0&_sort=-_lastUpdated&_total=accurate`;
  } else if (!ids) {
    queryString = `_count=20&_offset=0&_sort=-_lastUpdated&_total=accurate`;
  }
  const response = medplum.searchResources("Questionnaire", queryString);
  return response;
};


export async function getGeneratedQuestions(medplum: MedplumClient, patId: string, categoryCode: string) {
  const questArray = await twilioHandler(medplum, {
    eventType: "gen-qnr",
    patId,
    categoryCode
  });
  if (questArray?.length) return questArray;
  return null;
}



export function mapQuestionnaireToCardDetails(
  questionnaires: any,
  cardDetails: any,
  category?: boolean
) {
  const mappedResults: any = [];

  questionnaires.forEach((questionnaire: any) => {
    const codeValues = questionnaire?.code.map((c: any) =>
      c.code.toLowerCase()
    );

    cardDetails.forEach((card: any) => {
      const cardHeading = card?.heading?.toLowerCase();
      if (codeValues.some((code: any) => cardHeading?.toLowerCase() == code)) {
        if (category === true) {
          mappedResults.push({
            value: questionnaire?.id,
            label: card?.heading,
          });
        } else {
          mappedResults.push({
            id: questionnaire?.id,
            heading: card?.heading,
          });
        }
      }
    });
  });

  return mappedResults;
}
export async function reorderQuestionnaireIds(resources: any, idsToCheck: any) {
  return resources.sort((a: any, b: any) => {
    const aHasMatchingId = a?.questionnaireIds.some((id: any) =>
      idsToCheck?.includes(id)
    );
    const bHasMatchingId = b?.questionnaireIds.some((id: any) =>
      idsToCheck?.includes(id)
    );

    if (aHasMatchingId && !bHasMatchingId) return -1;
    if (!aHasMatchingId && bHasMatchingId) return 1;
    return 0;
  });
}

const twilioHandler = async (medplum: MedplumClient, payload: any) => {
  try {
    const res = await medplum.executeBot(
      {
        system: "https://api-stage.healthconnect.systems/bots",
        value: "twilio-service",
      },
      payload
    );
    if (res.statusCode > 300)
      showNotification({
        color: "red",
        message: `Something went wrong: ${res.body}`,
        autoClose: true,
      });
    return res.body;
  } catch (err) {
    showNotification({
      color: "red",
      message: `Something went wrong: ${err}`,
      autoClose: true,
    });
    console.error("Unexpected error:", err);
  }
};
const aiChatHandler = async (medplum: MedplumClient, payload: any) => {
  try {
    const res = await medplum.executeBot(
      {
        system: "https://api-stage.healthconnect.systems/bots",
        value: "ai-chat",
      },
      payload
    );
    if (res.statusCode > 300)
      showNotification({
        color: "red",
        message: `Something went wrong: ${res.body}`,
        autoClose: true,
      });
    return res.body;
  } catch (err) {
    showNotification({
      color: "red",
      message: `Something went wrong: ${err}`,
      autoClose: true,
    });
    console.error("Unexpected error:", err);
  }
};

export async function getQnrSummary(medplum: MedplumClient, qResId: string) {
  const summ = await twilioHandler(medplum, {
    eventType: "qnr-summary",
    qResId,
  });
  if (summ?.summary) return summ?.summary;
  return null;
}

export async function startCall(
  medplum: MedplumClient,
  patientId: string,
  qnrId: string,
  phoneNumber: string,
  linkIds: any,
  questionsText?: any,
  categoryName?: string
) {

  const call = await twilioHandler(medplum, {
    patientId,
    qnrId,
    phoneNumber,
    linkIds,
    questionsText,
    categoryName
  });
  showNotification({
    color: "green",
    message: `Call created Successfully with sid: ${call.sid} `,
  });
  return call;
}
export async function startChat(
  medplum: MedplumClient,
  patientId: string,
  questionnaireId: string,
  questionnaireLinkIds: any
) {
  if (questionnaireId.length === 0 || questionnaireLinkIds.length === 0) {
    return;
  } else {
    const call = await aiChatHandler(medplum, {
      request: "startChat",
      patientId,
      questionnaireId,
      questionnaireLinkIds,
    });

    showNotification({
      color: "green",
      message: `Chat Intiated Successfully `,
      autoClose: true,
    });
    return call;
  }
}
export async function sendMessage(
  medplum: MedplumClient,
  questionnaireResponseId: string,
  responseContent: string,
  currentLinkId: any
) {
  const chat = await aiChatHandler(medplum, {
    request: "processQuestionnaireResponse",
    questionnaireResponseId,
    responseContent,
    currentLinkId,
  });
  // showNotification({
  //   color: "green",
  //   message: `Chat Started Successfully `,
  // });
  return chat;
}

export async function retryCall(medplum: MedplumClient, qResId: string) {
  const call = await twilioHandler(medplum, {
    eventType: "qnr-resume",
    qResId,
  });
  showNotification({
    color: "green",
    message: `Call retried Successfully with sid: ${call.sid} `,
  });
  return call;
}

export const createQtrResponse = async (medplum: MedplumClient, data: any) => {
  try {
    const response = await medplum.createResource(data);
    showNotification({
      color: "green",
      message: "Response submitted successfully",
      autoClose: true,
    });
    return response;
  } catch (error) {
    console.error(error);
    showNotification({
      color: "red",
      message: "Failed to submit respponse",
      autoClose: true,
    });
  }
};
